/* Popup'lar: <newsletter-popup> (gecikme / %50 kaydırma) ve <exit-intent-popup> (çıkış niyeti).
   İkisi de içindeki <modal-dialog>'u (global.js) açar. Tema editöründe section seçilince açılır. */
(() => {
  if (customElements.get('newsletter-popup')) return;

  const DAY = 864e5;
  const store = {
    get(area, key) {
      try {
        return window[area].getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(area, key, value) {
      try {
        window[area].setItem(key, value);
      } catch (e) {
        /* depolama kapalıysa sessiz geç */
      }
    },
  };
  const designMode = () => !!(window.Shopify && window.Shopify.designMode);
  let selectedSection = null;

  /* Ortak taban */
  class CplPopup extends HTMLElement {
    get modal() {
      return this.querySelector('modal-dialog');
    }
    get isOpen() {
      return this.modal && !this.modal.hidden;
    }
    get key() {
      return 'cpl-popup-' + this.tagName.toLowerCase() + '-until';
    }

    connectedCallback() {
      this.onSelect = (e) => {
        selectedSection = e.detail.sectionId;
        if (e.detail.sectionId === this.dataset.sectionId) this.show(true);
      };
      this.onDeselect = (e) => {
        if (selectedSection === e.detail.sectionId) selectedSection = null;
        if (e.detail.sectionId === this.dataset.sectionId && this.isOpen) this.modal.close();
      };
      document.addEventListener('shopify:section:select', this.onSelect);
      document.addEventListener('shopify:section:deselect', this.onDeselect);

      if (designMode()) {
        // Ayar değişince section yeniden yüklenir; seçiliyse tekrar aç
        if (selectedSection === this.dataset.sectionId) customElements.whenDefined('modal-dialog').then(() => this.show(true));
        return;
      }
      if (this.dataset.active !== 'true') return;
      if (this.dataset.mobile !== 'true' && window.matchMedia('(max-width: 749px)').matches) return;
      this.init();
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:section:select', this.onSelect);
      document.removeEventListener('shopify:section:deselect', this.onDeselect);
      this.cleanup?.();
    }

    snoozed() {
      return parseInt(store.get('localStorage', this.key) || '0', 10) > Date.now();
    }
    snooze(days) {
      if (!days) return;
      const until = Date.now() + days * DAY;
      const prev = parseInt(store.get('localStorage', this.key) || '0', 10);
      store.set('localStorage', this.key, String(Math.max(prev, until)));
    }

    /* Yaş doğrulama, çerez bandı, açık çekmece/modal varken açılmaz */
    blocked() {
      return !!(
        document.querySelector('age-gate:not([hidden])') ||
        document.querySelector('cookie-banner:not([hidden])') ||
        document.querySelector('modal-dialog:not([hidden]), .drawer.is-open') ||
        document.body.classList.contains('overflow-hidden')
      );
    }

    async show(force = false) {
      if (!this.modal || this.isOpen) return false;
      await customElements.whenDefined('modal-dialog');
      if (!force && this.blocked()) return false;
      this.modal.open(document.activeElement);
      return true;
    }
  }

  /* <newsletter-popup> */
  class NewsletterPopup extends CplPopup {
    connectedCallback() {
      super.connectedCallback();
      this.bindForm();
    }

    init() {
      const formId = this.dataset.formId;
      const params = new URLSearchParams(window.location.search);
      const success = this.querySelector('[data-popup-success]');

      // Bu formdan dönüldüyse (başarı ya da hata) hemen aç
      if (formId && window.location.hash === '#' + formId) {
        if (success) this.snooze(365);
        this.show(true);
        return;
      }
      // Başka bir form gönderildiyse ya da zaten kayıtlıysa açma
      if (success || params.has('customer_posted') || this.snoozed()) return;

      this.modal.addEventListener('modal:close', () => this.snooze(parseInt(this.dataset.closeDays, 10) || 7));

      const trigger = () => this.tryOpen();
      this.timer = setTimeout(trigger, (parseInt(this.dataset.delay, 10) || 8) * 1000);
      if (this.dataset.scroll === 'true') {
        this.onScroll = () => {
          const max = document.documentElement.scrollHeight - window.innerHeight;
          if (max > 0 && window.scrollY / max >= 0.5) trigger();
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }
      this.cleanup = () => {
        clearTimeout(this.timer);
        clearTimeout(this.retry);
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      };
    }

    async tryOpen() {
      if (this.done || this.snoozed()) return this.cleanup?.();
      clearTimeout(this.retry);
      if (await this.show()) {
        this.done = true;
        this.cleanup?.();
      } else {
        // Engel kalkınca yeniden dene; yaş doğrulaması açıksa onaydan sonra tam gecikme kadar bekle
        const ageGate = document.querySelector('age-gate:not([hidden])');
        if (ageGate) {
          const wait = (parseInt(this.dataset.delay, 10) || 8) * 1000;
          const watch = setInterval(() => {
            if (!document.querySelector('age-gate:not([hidden])')) {
              clearInterval(watch);
              this.retry = setTimeout(() => this.tryOpen(), wait);
            }
          }, 500);
          return;
        }
        this.retry = setTimeout(() => this.tryOpen(), 4000);
      }
    }

    bindForm() {
      this.querySelector('[data-popup-dismiss]')?.addEventListener('click', () => {
        if (!designMode()) this.snooze(parseInt(this.dataset.dismissDays, 10) || 30);
        this.modal.close();
      });

      const form = this.querySelector('form');
      if (!form) return;
      const email = form.querySelector('[data-field="email"]');
      const consent = form.querySelector('[data-field="consent"]');
      const setError = (key, input, msg) => {
        const box = form.querySelector(`[data-error-for="${key}"]`);
        if (!input || !box) return;
        input.setAttribute('aria-invalid', msg ? 'true' : 'false');
        box.hidden = !msg;
        box.querySelector('span').textContent = msg || '';
      };
      const validate = () => {
        const v = (email?.value || '').trim();
        const e1 = !v ? this.dataset.errorEmailEmpty : /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v) ? '' : this.dataset.errorEmail;
        const e2 = consent && !consent.checked ? this.dataset.errorConsent : '';
        setError('email', email, e1);
        setError('consent', consent, e2);
        return !e1 && !e2;
      };
      form.addEventListener('submit', (e) => {
        this.tried = true;
        if (!validate()) {
          e.preventDefault();
          (email?.getAttribute('aria-invalid') === 'true' ? email : consent)?.focus();
        }
      });
      [email, consent].forEach((el) => el?.addEventListener('input', () => this.tried && validate()));
      consent?.addEventListener('change', () => this.tried && validate());
    }
  }

  /* <exit-intent-popup> */
  class ExitIntentPopup extends CplPopup {
    static SESSION_KEY = 'cpl-exit-intent-shown';

    init() {
      if (store.get('sessionStorage', ExitIntentPopup.SESSION_KEY) || this.snoozed()) return;
      const fire = () => this.tryOpen();
      const armedAt = Date.now() + 5000; // en az 5 sn sayfada kalınca
      const armed = () => Date.now() > armedAt;
      const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      if (fine) {
        this.onOut = (e) => {
          if (!e.relatedTarget && e.clientY <= 0 && armed()) fire();
        };
        document.addEventListener('mouseout', this.onOut);
      } else {
        // Hızlı yukarı kaydırma
        let lastY = window.scrollY;
        let lastT = performance.now();
        this.onScroll = () => {
          const y = window.scrollY;
          const t = performance.now();
          const speed = (lastY - y) / Math.max(1, t - lastT); // px/ms, yukarı pozitif
          if (armed() && speed > 2.5 && lastY > 400) fire();
          lastY = y;
          lastT = t;
          this.resetIdle();
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        // 45 sn hareketsizlik
        this.resetIdle = () => {
          clearTimeout(this.idle);
          this.idle = setTimeout(fire, 45000);
        };
        ['touchstart', 'keydown', 'click'].forEach((ev) => document.addEventListener(ev, this.resetIdle, { passive: true }));
        this.resetIdle();
      }

      this.cleanup = () => {
        if (this.onOut) document.removeEventListener('mouseout', this.onOut);
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
        if (this.resetIdle) ['touchstart', 'keydown', 'click'].forEach((ev) => document.removeEventListener(ev, this.resetIdle));
        clearTimeout(this.idle);
      };

      // Sete yükselt: popup'ı kapat, sepete ekleme cart-drawer.js'te
      this.addEventListener('submit', (e) => {
        if (e.target.matches('form[data-cart-ajax]')) this.modal.close();
      });
    }

    async tryOpen() {
      if (this.busy || this.done) return;
      if (this.blocked()) return; // bir sonraki tetiklemede tekrar denenir
      this.busy = true;
      try {
        const cart = await (window.CPL?.cart?.fetch() || Promise.resolve({ item_count: 0 }));
        if (this.dataset.onlyCart === 'true' && !cart.item_count) return;
        await this.refresh();
        if (await this.show()) {
          this.done = true;
          store.set('sessionStorage', ExitIntentPopup.SESSION_KEY, '1');
          this.snooze(parseInt(this.dataset.frequencyDays, 10) || 0);
          this.cleanup?.();
        }
      } catch (e) {
        /* ağ hatasında sessiz geç */
      } finally {
        this.busy = false;
      }
    }

    /* Güncel sepetle içeriği yeniden çiz (Section Rendering API) */
    async refresh() {
      const id = this.dataset.sectionId;
      const res = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(id)}`);
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('[data-popup-content]');
      const current = this.querySelector('[data-popup-content]');
      if (fresh && current) {
        current.replaceWith(fresh);
        fresh.querySelectorAll('[data-modal-close]').forEach((b) => b.addEventListener('click', () => this.modal.close()));
      }
    }
  }

  customElements.define('newsletter-popup', NewsletterPopup);
  customElements.define('exit-intent-popup', ExitIntentPopup);
})();
